import React, { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';

import { IEventMessage } from "@eagerdog/interfaces";

import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";

import "./SendMessage.scss";
import Input from "../Input/Input";

interface IProps {
  handleSendMessage: (formData: IEventMessage) => void
  subject: string
  messageBody?: string
  topText?: JSX.Element
  type: SendMessageType
  isSending: boolean
}

export enum SendMessageType {
  Registrants = "registrants",
  BulkEmailResults = "bulk_email_results",
  EmailResults = "email_results",
  ResendRegistration = "resend_registration",
}

const SendMessage: React.FC<IProps> = (props) => {
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [attachFinalConfirmation, setAttachFinalConfirmation] = useState<boolean>(false);
  const [addArmBands, setAddArmBands] = useState<boolean>(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [customEmailsField, setCustomEmailsField] = useState<string>("");
  const [customSubjectField, setCustomSubjectField] = useState<string>(props.subject);
  const [onlyQualified, setOnlyQualified] = useState<boolean>(false);

  const handleAttachmentSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!validateAttachmentsSize(file)) return;
      setAttachedFiles([file, ...attachedFiles]);
      // NOTE: allows to select the same file after it has been removed for example
      // we dont use inputs values anyway
      event.target.value = "";
    }
  }

  const handleAttachmentRemove = (file: File) => {
    setAttachedFiles(attachedFiles.filter((attachment) => {
      return attachment.name !== file.name && attachment.lastModified !== file.lastModified;
    }));
  }

  // NOTE: check on current attachments + new input file
  const validateAttachmentsSize = (file: File): boolean => {
    const maxSize = 1e+7; // 10 MB
    const summary: number = attachedFiles.map((attachment) => attachment.size)
      .reduce((a, b) => a + b, file.size);
    if (summary > maxSize) {
      toast.show({
        title: "Attachments limit",
        content: "Overall files size should not exceed 10 megabytes",
        duration: 10000,
        type: "fail"
      });
      return false;
    }
    return true;
  }

  const truncateFileName = (file: File) => {
    const name = file.name;
    const maxLength = 14;
    if (name.length > maxLength) return name.slice(0, maxLength - 1) + "…";
    return name;
  }

  const MAX_SAFE_SIZE = 400000; // 400 KB safe threshold

  const checkPayloadSize = (message: IEventMessage): boolean => {
    const { content, attachments = [], attach_registration_summary, add_armbands } = message;

    // Estimate size of message content
    const messageSize = new Blob([content]).size;

    // Estimate size of attachments (base64 overhead = ~33%)
    const attachmentSize = attachments.reduce((total, file) => {
      return total + file.size * 1.33;
    }, 0);

    // Estimate generated PDF size for optional flags (rough approximation)
    let estimatedPdfSize = 0;
    if (attach_registration_summary) estimatedPdfSize += 150000; // ~150KB
    if (add_armbands) estimatedPdfSize += 100000; // ~100KB

    const totalSize = messageSize + attachmentSize + estimatedPdfSize;

    return totalSize <= MAX_SAFE_SIZE;
  };

  const handleSubmitSendMessage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(false)
  };

  const handleSubmitTestSendMessage = () => {
    handleSubmit(true)
  };

  const handleSubmit = (isTestSending?: boolean) => {
    if (!editorRef.current) return;
  
    const content = editorRef.current.getContent();
  
    if (!content) {
      toast.show({
        title: "Event Message",
        content: "Please type in a message",
        duration: 10000,
        type: "fail"
      });
      return;
    }

    const rawEmails = customEmailsField.split(',').map(email => email.trim());
    const customEmails = rawEmails.filter(email => !!email); // убираем пустые
    const payload: IEventMessage = {
      content,
      attach_registration_summary: attachFinalConfirmation,
      add_armbands: addArmBands,
      attachments: attachedFiles,
      custom_emails: customEmails,
      custom_subject: customSubjectField ?? undefined,
      is_test_sending: isTestSending,
      only_qualified: onlyQualified
    };

    // TODO: remove when rework attachments handle for all email types
    if (props.type !== SendMessageType.Registrants && !checkPayloadSize(payload)) {
      toast.show({
        title: "Message too large",
        content: "Your message and attachments may be too large to send. Try shortening the content or removing files.",
        duration: 10000,
        type: "fail"
      });
      return;
    }
  
    props.handleSendMessage(payload);
  };

  return (
    <form className='SendMessage' onSubmit={handleSubmitSendMessage}>
      <div className="title">Send Message</div>
      <div className="formWrap">
        {props.topText}
        {[SendMessageType.BulkEmailResults, SendMessageType.Registrants].includes(props.type) && (
          <Input
            autoComplete="off"
            type="text"
            label="Add additional recipients (comma separated)"
            onChange={(e) => { setCustomEmailsField(e.target.value); }}
            defaultValue={customEmailsField}
            placeholder="users1@email.com, users2@email.com"
          />
        )}
        <Input
          autoComplete="off"
          type="text"
          label="Subject"
          onChange={(e) => { setCustomSubjectField(e.target.value); }}
          defaultValue={customSubjectField}
          placeholder={props.subject}
        />
        <Editor
          apiKey='e4kmr66e3tqyrdxqk84c9ucybc5l3qzaum53urpf5q3ccg54'
          onInit={(_evt, editor) => editorRef.current = editor}
          init={{
            plugins: 'emoticons link lists searchreplace table visualblocks wordcount paste',
            toolbar: 'undo redo | fontfamily fontsize | bold italic underline strikethrough | link image mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
            block_unsupported_drop: true,
            paste_data_images: false,          // Disallow pasting images
            automatic_uploads: false,          // Disable automatic image uploads
            images_upload_handler: () => {
              return Promise.reject("Image uploads are disabled.");
            },   // Override upload handler to prevent drag/drop uploads
            file_picker_types: "",  
            tinycomments_mode: 'embedded',
            tinycomments_author: 'Author name',
            menubar: false,
            branding: false,
            mergetags_list: [
              { value: 'First.Name', title: 'First Name' },
              { value: 'Email', title: 'Email' },
            ],
          }}
          initialValue={props.messageBody ?? ""}
        />
        <div className="inputsWrap">
          <div className="checkWrap">
            {props.type === SendMessageType.Registrants && (
              <>
                <Checkbox
                  id="attachFinalConfirmation"
                  label="Attach Registrants Final Confirmation to Email"
                  value={attachFinalConfirmation}
                  onChange={() => {
                    setAttachFinalConfirmation(!attachFinalConfirmation);
                  }}
                />
                <Checkbox
                  id="addArmBands"
                  label="Include Armbands"
                  value={addArmBands}
                  onChange={() => {
                    setAddArmBands(!addArmBands);
                  }}
                />
              </>
            )}
            {props.type === SendMessageType.BulkEmailResults && (
              <Checkbox
                id="onlyQualified"
                label="Only email Qualified results"
                value={onlyQualified}
                onChange={() => {
                  setOnlyQualified(!onlyQualified);
                }}
              />
            )}
          </div>
          <div className="actions">
            <Button type="button" disabled={attachedFiles.length >= 3} onClick={(event) => {
              event.stopPropagation();
              uploadInputRef?.current?.click();
            }}>
              Upload attachment
              <input
                ref={uploadInputRef}
                style={{ display: 'none' }}
                type='file'
                accept='image/*, application/pdf'
                onChange={handleAttachmentSelect}
              />
            </Button>
            <div className="attachedFiles">
              {attachedFiles.map((file, index) => (
                <Button key={index} type="button" className="attachment">
                  {truncateFileName(file)}
                  <span className="icon" onClick={() => handleAttachmentRemove(file)}></span>
                </Button>
              ))}
            </div>
            <div className="buttons">
              <Button
                className="submit"
                type="button"
                onClick={handleSubmitTestSendMessage}
                disabled={props.isSending}
              >
                Send Test
              </Button>
              <Button type="submit" className="submit" isLoading={props.isSending}>Send Message</Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default SendMessage;
